import { useState } from 'react'
import { Link, NavLink, Navigate, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, ChevronDown, ChevronRight } from 'lucide-react'
import MainLayout from '../components/Layout/MainLayout'
import ReserveWelcomePackCompleteModal from '../components/ReserveWelcomePackCompleteModal'
import WelcomePackTopicContent from '../components/WelcomePackTopicContent'
import { ACCOUNT_PAGES_HERO_IMAGE_URL } from '../constants/accountPagesHeroImage'
import { getWelcomePackHandbookIntro } from '../constants/welcomePackHandbookIntros'
import {
  getReserveWelcomePackSection,
  RESERVE_MEMBERSHIP_GUIDE_PATH,
  RESERVE_WELCOME_PACK_PATH,
  RESERVE_WELCOME_PACK_SECTIONS,
  RESERVE_WELCOME_PACK_DEFAULT_TOPIC_ID,
  RESERVE_WELCOME_PACK_LAST_TOPIC_ID,
} from '../constants/reserveWelcomePack'

const baseTopicPath = (id) => `${RESERVE_WELCOME_PACK_PATH}/${id}`

const ReserveWelcomePackHandbookPage = () => {
  const { topicId } = useParams()
  const navigate = useNavigate()
  const [mobileNavOpen, setMobileNavOpen] = useState(true)
  const [completeModalOpen, setCompleteModalOpen] = useState(false)

  const section = getReserveWelcomePackSection(topicId)

  if (!section) {
    return <Navigate to={baseTopicPath(RESERVE_WELCOME_PACK_DEFAULT_TOPIC_ID)} replace />
  }

  const intro = getWelcomePackHandbookIntro(section.id)
  const idx = RESERVE_WELCOME_PACK_SECTIONS.findIndex((s) => s.id === section.id)
  const nextSections = []
  for (let k = 1; k <= RESERVE_WELCOME_PACK_SECTIONS.length && nextSections.length < 3; k += 1) {
    const j = (idx + k) % RESERVE_WELCOME_PACK_SECTIONS.length
    const s = RESERVE_WELCOME_PACK_SECTIONS[j]
    if (s.id !== section.id) nextSections.push(s)
  }
  const nextTopic =
    idx >= 0 && idx < RESERVE_WELCOME_PACK_SECTIONS.length - 1
      ? RESERVE_WELCOME_PACK_SECTIONS[idx + 1]
      : RESERVE_WELCOME_PACK_SECTIONS[0]

  const isLastWelcomePackTopic = section.id === RESERVE_WELCOME_PACK_LAST_TOPIC_ID

  const handleCompleteModalContinue = () => {
    setCompleteModalOpen(false)
    navigate(baseTopicPath(RESERVE_WELCOME_PACK_DEFAULT_TOPIC_ID))
  }

  const handleCompleteModalGuide = () => {
    setCompleteModalOpen(false)
    navigate(RESERVE_MEMBERSHIP_GUIDE_PATH)
  }

  const topicNavLink = ({ isActive }) =>
    `group flex w-full items-center justify-between gap-3 border-b border-neutral-200 px-5 py-4 text-left text-[15px] transition-colors duration-150 ${
      isActive
        ? 'bg-neutral-50 font-poppinsSemiBold text-black'
        : 'font-poppinsMedium text-neutral-900 hover:bg-neutral-50/90'
    }`

  return (
    <MainLayout>
      <ReserveWelcomePackCompleteModal
        isOpen={completeModalOpen}
        onClose={() => setCompleteModalOpen(false)}
        onContinueToStart={handleCompleteModalContinue}
        onGoToGuide={handleCompleteModalGuide}
      />
      <div className="min-h-screen min-w-0 w-full overflow-x-clip bg-neutral-50">
        <section className="relative min-h-[13rem] sm:min-h-[15rem] bg-black overflow-hidden pb-6">
          <div className="h-12" />
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-55"
            style={{ backgroundImage: `url("${ACCOUNT_PAGES_HERO_IMAGE_URL}")` }}
            aria-hidden
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/15 to-black/40" />
          <div className="relative z-10 px-4 sm:px-6 lg:px-8 pt-2">
            <div className="max-w-7xl mx-auto w-full">
              <nav className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-white/55 mb-4" aria-label="Breadcrumb">
                <Link to={RESERVE_MEMBERSHIP_GUIDE_PATH} className="hover:text-white transition-colors">
                  Guide
                </Link>
                <span aria-hidden className="text-white/35">
                  /
                </span>
                <Link to={RESERVE_WELCOME_PACK_PATH} className="hover:text-white transition-colors">
                  Welcome pack
                </Link>
                <span aria-hidden className="text-white/35">
                  /
                </span>
                <span className="text-white/80 truncate max-w-[12rem] sm:max-w-none">{section.title}</span>
              </nav>
              <Link
                to={RESERVE_MEMBERSHIP_GUIDE_PATH}
                className="inline-flex items-center gap-2 text-sm font-medium text-white/70 hover:text-white mb-4 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden />
                Back to membership guide
              </Link>
              <h1 className="text-2xl sm:text-3xl md:text-4xl typography-hero-title-on-dark font-bold text-white leading-tight max-w-4xl">
                {section.title}
              </h1>
            </div>
          </div>
        </section>

        <div className="max-w-[1600px] mx-auto px-0 sm:px-4 lg:px-6 xl:px-8 py-8 lg:py-10">
          <div className="flex flex-col gap-0 lg:flex-row lg:items-start lg:gap-0">
            {/* Handbook panel — sticky to viewport; header stays put, topic list scrolls inside */}
            <aside className="w-full shrink-0 border-y border-neutral-200 bg-white sm:border-x sm:rounded-none lg:rounded-none lg:border-y-0 lg:border-r lg:border-l-0 lg:sticky lg:top-24 lg:z-20 lg:flex lg:w-[300px] lg:max-h-[calc(100dvh-6.5rem)] lg:flex-col lg:overflow-hidden lg:self-start lg:shadow-[6px_0_32px_-12px_rgba(0,0,0,0.08)] xl:w-[320px]">
              <div className="shrink-0 border-b border-neutral-200 px-5 pb-5 pt-6 sm:px-6 sm:pt-8">
                <h2 className="m-0 text-xl font-bold leading-tight tracking-tight text-black sm:text-2xl font-AXONALLPrimeSemiBold">
                  Welcome pack
                </h2>
                <p className="mt-2 mb-0 max-w-[16rem] text-xs leading-relaxed text-neutral-500 font-poppinsLight">
                  Select a topic to read. This panel stays visible while you scroll.
                </p>
              </div>

              {/* Mobile: collapse header */}
              <button
                type="button"
                onClick={() => setMobileNavOpen((v) => !v)}
                className="flex w-full shrink-0 items-center justify-between border-b border-neutral-200 px-5 py-4 text-left lg:hidden"
                aria-expanded={mobileNavOpen}
              >
                <span className="text-sm font-poppinsSemiBold text-black">Topics</span>
                <ChevronDown
                  className={`h-5 w-5 shrink-0 text-neutral-500 transition-transform duration-200 ${mobileNavOpen ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </button>

              <nav
                className={`${mobileNavOpen ? 'block' : 'hidden'} min-h-0 lg:flex lg:flex-1 lg:flex-col lg:min-h-0 lg:overflow-y-auto`}
                aria-label="Welcome pack topics"
              >
                {/* Desktop accordion row (visual match to handbook) */}
                <div className="hidden border-b border-neutral-200 px-5 py-4 lg:block">
                  <div
                    className="flex w-full items-center justify-between gap-3 text-left"
                    role="presentation"
                  >
                    <span className="text-sm font-poppinsSemiBold text-black">All topics</span>
                    <ChevronDown className="h-5 w-5 shrink-0 text-neutral-400 rotate-180" aria-hidden />
                  </div>
                </div>

                <div className="pb-2 lg:pb-6">
                  {RESERVE_WELCOME_PACK_SECTIONS.map((s) => (
                    <NavLink key={s.id} to={baseTopicPath(s.id)} className={topicNavLink}>
                      <span className="min-w-0 flex-1 leading-snug">{s.title}</span>
                      <ChevronDown
                        className="h-4 w-4 shrink-0 text-neutral-400 transition-transform group-hover:text-neutral-600 lg:rotate-[-90deg]"
                        aria-hidden
                      />
                    </NavLink>
                  ))}
                </div>
              </nav>
            </aside>

            {/* Main + right column */}
            <div className="flex min-w-0 flex-1 flex-col gap-8 lg:flex-row lg:gap-10 lg:pl-8 lg:pr-2 xl:pl-10">
              <main className="min-w-0 flex-1 max-w-3xl px-4 sm:px-0">
                {intro ? (
                  <div className="mb-8 rounded-lg border border-neutral-200 bg-white px-5 py-4 sm:px-6 sm:py-5 shadow-sm">
                    <p className="font-poppinsLight text-[15px] sm:text-base leading-relaxed text-neutral-800 m-0">
                      {intro}
                    </p>
                  </div>
                ) : null}
                <WelcomePackTopicContent section={section} />
              </main>

              <aside className="hidden w-full shrink-0 px-4 sm:px-0 lg:flex lg:w-[200px] xl:w-[220px] lg:sticky lg:top-24 lg:z-20 lg:max-h-[calc(100dvh-6.5rem)] lg:flex-col lg:overflow-hidden lg:self-start lg:border-l lg:border-neutral-200 lg:px-0 lg:pl-6 lg:border-t-0 lg:pt-0">
                <h2 className="shrink-0 text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-4">
                  What&apos;s next
                </h2>
                <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                  <ul className="space-y-3 m-0 p-0 list-none">
                    {nextSections.map((s) => (
                      <li key={s.id}>
                        <Link
                          to={baseTopicPath(s.id)}
                          className="group flex items-start gap-2 text-sm text-neutral-700 leading-snug hover:text-black"
                        >
                          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-neutral-300 transition-transform group-hover:translate-x-0.5 group-hover:text-neutral-600" aria-hidden />
                          <span className="underline-offset-2 group-hover:underline">{s.title}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  {nextTopic ? (
                    isLastWelcomePackTopic ? (
                      <button
                        type="button"
                        onClick={() => setCompleteModalOpen(true)}
                        className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-neutral-800 bg-neutral-950 px-4 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider text-white transition hover:bg-black"
                      >
                        Finish welcome pack
                        <ChevronRight className="h-4 w-4" aria-hidden />
                      </button>
                    ) : (
                      <Link
                        to={baseTopicPath(nextTopic.id)}
                        className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-neutral-800 bg-neutral-950 px-4 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider text-white transition hover:bg-black"
                      >
                        Next topic
                        <ChevronRight className="h-4 w-4" aria-hidden />
                      </Link>
                    )
                  ) : null}
                </div>
              </aside>
            </div>
          </div>

          <div className="mx-4 mt-8 rounded-lg border border-neutral-200 bg-white p-4 shadow-sm sm:mx-6 lg:hidden">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-3">
              What&apos;s next
            </h2>
            <ul className="space-y-2 m-0 p-0 list-none">
              {nextSections.map((s) => (
                <li key={s.id}>
                  <Link
                    to={baseTopicPath(s.id)}
                    className="text-sm text-neutral-700 hover:text-black underline-offset-2 hover:underline"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
            {nextTopic ? (
              isLastWelcomePackTopic ? (
                <button
                  type="button"
                  onClick={() => setCompleteModalOpen(true)}
                  className="mt-6 inline-flex w-full items-center justify-center rounded-md border border-neutral-300 bg-neutral-50 px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-neutral-800"
                >
                  Finish welcome pack
                </button>
              ) : (
                <Link
                  to={baseTopicPath(nextTopic.id)}
                  className="mt-6 inline-flex w-full items-center justify-center rounded-md border border-neutral-300 bg-neutral-50 px-4 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-neutral-800"
                >
                  Next topic
                </Link>
              )
            ) : null}
          </div>
        </div>
      </div>
    </MainLayout>
  )
}

export default ReserveWelcomePackHandbookPage
